import { useEffect, useState } from "react";
import { Plus, Trash2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, money } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SerialSelect } from "@/components/SerialSelect";
import { RollAllocator } from "@/components/RollAllocator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const REASONS = ["Studio Error", "Installer Error", "Material Defect", "Other"];
const PARTIES = ["Studio", "Installer", "Supplier"];
const EMPTY = {
  material_name: "",
  quantity: "",
  reason: "Studio Error",
  responsible_party: "Studio",
  installer_id: "",
  notes: "",
};

// Wastage is logged separately from standard consumption and deducted additively.
export const JobWastage = ({ job, canEdit, installers = [], onChange }) => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [stock, setStock] = useState([]);

  const catOf = (name) => (stock.find((s) => s.name === name) || {}).category || "";

  const materials = [
    ...new Set([
      ...(job.estimated_consumption || []).map((c) => c.name),
      ...(job.consumption || []).map((c) => c.name),
    ]),
  ];

  const load = () => {
    api.get(`/jobs/${job.id}/wastage`).then(({ data }) => setRows(data));
  };
  useEffect(() => {
    load();
    api.get(`/stock?studio_id=${job.studio_id}`).then(({ data }) => setStock(data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id]);

  const submit = async () => {
    try {
      await api.post(`/jobs/${job.id}/wastage`, {
        material_name: form.material_name,
        quantity: Number(form.quantity),
        reason: form.reason,
        responsible_party: form.responsible_party,
        ...(form.responsible_party === "Installer" && form.installer_id
          ? { installer_id: form.installer_id }
          : {}),
        notes: form.notes,
        ...(form.allocations && form.allocations.length
          ? { serial_allocations: form.allocations.map((a) => ({ serial_id: a.serial_id, quantity: Number(a.quantity) })) }
          : { serial_id: form.serial_id || null }),
      });
      toast.success("Wastage logged and deducted from stock");
      setForm(EMPTY);
      setOpen(false);
      load();
      onChange?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const remove = async (r) => {
    try {
      await api.delete(`/jobs/${job.id}/wastage/${r.id}`);
      toast.success("Entry removed and stock restored");
      load();
      onChange?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const total = rows.reduce((a, r) => a + (r.value || 0), 0);

  return (
    <Card className="border-brand-line shadow-none" data-testid="job-wastage-card">
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-lg">Wastage</CardTitle>
          <p className="mt-1 text-xs text-slate-500">
            Logged in addition to standard consumption — total deducted from stock is consumption +
            wastage. {rows.length} entr{rows.length === 1 ? "y" : "ies"} · {money(total)} at HQ cost.
          </p>
        </div>
        {canEdit && (
          <Button
            size="sm"
            variant="outline"
            className="min-h-[44px]"
            data-testid="add-wastage-btn"
            onClick={() => setOpen(!open)}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" /> {open ? "Close" : "Log wastage"}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {open && canEdit && (
          <div className="grid gap-3 rounded-md bg-slate-50/70 p-3 sm:grid-cols-2">
            <div>
              <Label>Material *</Label>
              <Select
                value={form.material_name}
                onValueChange={(v) => setForm((f) => ({ ...f, material_name: v }))}
              >
                <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="wastage-material-select">
                  <SelectValue placeholder="Material used on this job" />
                </SelectTrigger>
                <SelectContent>
                  {materials.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quantity wasted *</Label>
              <Input
                className="mt-1.5 min-h-[44px]"
                inputMode="decimal"
                placeholder="e.g. 0.050"
                data-testid="wastage-qty-input"
                value={form.quantity}
                onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
              />
            </div>
            {["PPF", "Sunfilm"].includes(catOf(form.material_name)) && (
              <div className="sm:col-span-2">
                <Label>Roll serials (sqft deducted)</Label>
                <div className="mt-1.5">
                  <RollAllocator
                    studioId={job.studio_id}
                    category={catOf(form.material_name)}
                    totalQty={Number(form.quantity) || 0}
                    value={form.allocations || []}
                    testidPrefix="wastage-roll"
                    onChange={(allocs) => setForm((f) => ({ ...f, allocations: allocs }))}
                  />
                </div>
              </div>
            )}
            <div>
              <Label>Reason</Label>
              <Select
                value={form.reason}
                onValueChange={(v) => setForm((f) => ({ ...f, reason: v }))}
              >
                <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="wastage-reason-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REASONS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Responsible party</Label>
              <Select
                value={form.responsible_party}
                onValueChange={(v) => setForm((f) => ({ ...f, responsible_party: v }))}
              >
                <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="wastage-party-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PARTIES.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.responsible_party === "Installer" && (
              <div>
                <Label>Which installer</Label>
                <Select
                  value={form.installer_id}
                  onValueChange={(v) => setForm((f) => ({ ...f, installer_id: v }))}
                >
                  <SelectTrigger
                    className="mt-1.5 min-h-[44px]"
                    data-testid="wastage-installer-select"
                  >
                    <SelectValue placeholder="Installer on this job" />
                  </SelectTrigger>
                  <SelectContent>
                    {installers.map((i) => (
                      <SelectItem key={i.id} value={i.id}>
                        {i.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="sm:col-span-2">
              <Label>Notes</Label>
              <Textarea
                className="mt-1.5"
                rows={2}
                data-testid="wastage-notes-input"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
            <Button
              className="min-h-[44px] sm:col-span-2"
              data-testid="save-wastage-btn"
              onClick={submit}
              disabled={!form.material_name || !(Number(form.quantity) > 0)}
            >
              Log wastage entry
            </Button>
          </div>
        )}

        {rows.length === 0 && (
          <p className="py-4 text-center text-sm text-slate-500">No wastage logged on this job.</p>
        )}
        {rows.map((r) => (
          <div
            key={r.id}
            className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-brand-line p-3"
            data-testid={`wastage-row-${r.id}`}
          >
            <div className="min-w-0">
              <p className="text-sm font-semibold text-brand-ink">
                {r.material_name} · {Number(r.quantity).toFixed(3)} {r.unit}
                <span className="ml-2 font-normal text-slate-500">{money(r.value)}</span>
              </p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                <Badge className="border-0 bg-amber-50 text-amber-700">{r.reason}</Badge>
                <Badge className="border-0 bg-slate-100 text-slate-600">
                  {r.responsible_party}
                  {r.installer_name ? ` · ${r.installer_name}` : ""}
                </Badge>
                {r.short > 0 && (
                  <Badge className="border-0 bg-crimson-50 text-crimson-700">
                    <TriangleAlert className="mr-1 h-3 w-3" /> {r.short} {r.unit} not in stock
                  </Badge>
                )}
              </div>
              {r.notes && <p className="mt-1 text-xs text-slate-500">{r.notes}</p>}
            </div>
            {canEdit && (
              <Button
                size="sm"
                variant="ghost"
                className="text-rose-600"
                data-testid={`delete-wastage-${r.id}`}
                onClick={() => remove(r)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
